import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { DetailsValues } from '../state';
import { frameTimeline, isFrameFromCreation, projectDuration } from '../state/detailsView';
import { formatDay, formatDayMonth, formatRange } from '../state/formatDates';

import { DetailsCard, ValueNote } from './DetailsCard';
import type { CardEditProps } from './DetailsCard';
import { ResourcePlanningFacts } from './ResourcePlanningFacts';

import { MonoText } from '@/components/ui';

/**
 * When (brief §3): the date range and its length, "· set at creation" while
 * the dates are the project's own, then a bar with today and the delivery date
 * always marked (Mette, 1 Nov) — the bar stretches to take them in when they
 * fall outside the frame. Today is the mark alone; the delivery date is also
 * named under the bar (2 Nov). Under them, Resource planning — moved here from
 * Where it belongs (decision 18).
 */

/** The frame's bar and its elapsed part. */
const BAR_SX = {
  position: 'absolute',
  top: 6,
  height: 4,
  borderRadius: 'var(--border-radius-small)',
} as const;

/**
 * The delivery date's mark: a 10px diamond in the library's info blue — clear
 * of today's navy line, and not the pink of the primary button (Mette, 2 Nov).
 */
const DIAMOND_SX = {
  width: 10,
  height: 10,
  ml: '-5px',
  flex: 'none',
  transform: 'rotate(45deg)',
  borderRadius: '2px',
  bgcolor: 'var(--color-blue-80)',
} as const;
export type WhenCardProps = {
  values: Pick<
    DetailsValues,
    'startDate' | 'endDate' | 'deliveryDate' | 'includeInResourcePlanning' | 'likelihoodPct'
  >;
  defaults: Pick<DetailsValues, 'startDate' | 'endDate'>;
  /** ISO day — injected, so the bar is testable. */
  today: string;
  /** Edit mode, for a card that has its form. */
  edit?: CardEditProps;
};

export const WhenCard = ({ values, defaults, today, edit }: WhenCardProps) => {
  const { t } = useTranslation('pmDetails');
  const { startDate, endDate, deliveryDate } = values;
  const hasFrame = startDate !== '' && endDate !== '';
  const duration = projectDuration(startDate, endDate);
  const bar = frameTimeline(startDate, endDate, today, deliveryDate);

  return (
    <DetailsCard
      icon="calendar_month"
      title={t('when.Title')}
      automationId="details-card-when"
      {...edit}
    >
      <Stack sx={{ gap: 2 }}>
        {hasFrame ? (
          <Stack direction="row" sx={{ alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
            <Typography
              variant="bodyRegular"
              component="span"
              data-automation-id="details-when-range"
            >
              {formatRange(startDate, endDate)}
            </Typography>
            {duration ? (
              <Typography
                variant="bodyRegular"
                component="span"
                sx={{ color: 'var(--color-fog-80)' }}
                data-automation-id="details-when-duration"
              >
                ·{' '}
                {t(duration.unit === 'days' ? 'when.Days' : 'when.Months', {
                  count: duration.count,
                })}
              </Typography>
            ) : null}
            {isFrameFromCreation(values, defaults) ? (
              <ValueNote automationId="details-when-note">· {t('when.SetAtCreation')}</ValueNote>
            ) : null}
          </Stack>
        ) : (
          <Typography variant="bodyRegular" sx={{ color: 'var(--color-fog-80)' }}>
            {t('when.NoDates')}
          </Typography>
        )}

        {bar ? (
          <Stack sx={{ gap: 0.75 }} data-automation-id="details-when-progress">
            <Box sx={{ position: 'relative', height: 16 }}>
              {/* The span beyond the frame: a hairline, so today and the delivery have room. */}
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: 7,
                  height: 2,
                  bgcolor: 'var(--color-fog-20)',
                }}
              />
              <Box
                data-automation-id="details-when-frame"
                sx={{
                  ...BAR_SX,
                  left: `${String(bar.frame.left)}%`,
                  width: `${String(bar.frame.right - bar.frame.left)}%`,
                  bgcolor: 'var(--color-fog-40)',
                }}
              />
              <Box
                sx={{
                  ...BAR_SX,
                  left: `${String(bar.frame.left)}%`,
                  width: `${String(bar.elapsedRight - bar.frame.left)}%`,
                  bgcolor: 'var(--color-fog-60)',
                }}
              />
              {bar.delivery !== null ? (
                <Box
                  role="img"
                  aria-label={t('when.Delivery', { date: formatDay(deliveryDate) })}
                  title={t('when.Delivery', { date: formatDay(deliveryDate) })}
                  data-automation-id="details-when-delivery-mark"
                  sx={{
                    position: 'absolute',
                    top: 3,
                    left: `${String(bar.delivery)}%`,
                    ...DIAMOND_SX,
                  }}
                />
              ) : null}
              <Box
                role="img"
                aria-label={t('when.TodayTitle', { date: formatDay(today) })}
                title={t('when.TodayTitle', { date: formatDay(today) })}
                data-automation-id="details-when-today"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: `${String(bar.today)}%`,
                  width: 2,
                  height: 16,
                  ml: '-1px',
                  borderRadius: '1px',
                  bgcolor: 'var(--color-navy-100)',
                }}
              />
            </Box>
            {/* The frame's own dates, under its ends. */}
            <Box sx={{ position: 'relative', height: 16 }}>
              <Box sx={{ position: 'absolute', left: `${String(bar.frame.left)}%` }}>
                <MonoText size="label" color="var(--color-fog-80)">
                  {formatDayMonth(startDate)}
                </MonoText>
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  left: `${String(bar.frame.right)}%`,
                  transform: 'translateX(-100%)',
                  whiteSpace: 'nowrap',
                }}
              >
                <MonoText size="label" color="var(--color-fog-80)">
                  {formatDayMonth(endDate)}
                </MonoText>
              </Box>
            </Box>
          </Stack>
        ) : null}

        {/* The key: the delivery date, as its mark says it (today is the mark alone). */}
        <Stack
          direction="row"
          sx={{ alignItems: 'center', columnGap: 3, rowGap: 0.5, flexWrap: 'wrap' }}
        >
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
            {deliveryDate !== '' && bar ? <Box aria-hidden sx={DIAMOND_SX} /> : null}
            <Typography
              variant="bodyRegular"
              component="span"
              data-automation-id="details-when-delivery"
              sx={{ color: deliveryDate === '' ? 'var(--color-fog-80)' : undefined }}
            >
              {deliveryDate === ''
                ? t('when.NoDelivery')
                : t('when.Delivery', { date: formatDay(deliveryDate) })}
            </Typography>
          </Stack>
        </Stack>
        <ResourcePlanningFacts values={values} />
      </Stack>
    </DetailsCard>
  );
};
